'use client';

import { GTM_NOTSET_STRING, getGtmAttr } from '@musinsa/frontend-utils';
import { Button, Divider, IcCopy, IcDownload, Label, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import type { CouponItemDto } from '@musinsa/services-curator';
import { useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import { useCouponLinkCopy } from '../../common/hooks';
import { trackMyShopCouponCopyLinkClickMusinsa } from '../utils/couponMusinsaAnalytics';

interface CouponCardProps {
  coupon: CouponItemDto;
  snsTitle: string;
  onReceive?: (couponNo: number) => void;
  /**
   * 링크 복사 버튼 노출 여부 (owner only). 클릭 시 카드 내부에서 short link 사전
   * fetch 캐시를 동기 read 하여 클립보드 복사 (webview Safari 정책 회피).
   */
  enableLinkCopy?: boolean;
  onClickAppliedGoods?: () => void;
  disabled?: boolean;
  className?: string;
}

export const CouponCard = ({
  coupon,
  snsTitle,
  onReceive,
  enableLinkCopy = false,
  onClickAppliedGoods,
  disabled = false,
  className,
}: CouponCardProps) => {
  const { service } = useDeviceInfo();
  const isExhausted = coupon.status === 'EXHAUSTED';
  const copyLink = useCouponLinkCopy({
    couponNo: coupon.couponNo,
    enabled: enableLinkCopy,
  });
  const handleCopyLink = useCallback(() => {
    if (enableLinkCopy && service === SERVICE.MUSINSA) {
      trackMyShopCouponCopyLinkClickMusinsa({
        brandId: coupon.brandId,
        couponId: coupon.couponNo,
        couponName: coupon.couponName,
        curatorId: snsTitle,
      });
    }
    copyLink();
  }, [
    copyLink,
    coupon.brandId,
    coupon.couponName,
    coupon.couponNo,
    enableLinkCopy,
    service,
    snsTitle,
  ]);
  const { className: gtmClassName, ...gtmDataAttrs } = getGtmAttr(['impression_content'], {
    brand_id: coupon.brandId,
    content_id: String(coupon.couponNo),
    content_name: coupon.couponName,
    content_profile_id: snsTitle,
    content_type: String(coupon.salesRate),
    index: GTM_NOTSET_STRING,
    section_index: '2',
    section_name: 'curator_collaboration',
  });

  const renderActionArea = () => {
    if (enableLinkCopy) {
      // NOTE: disabled prop 은 방문자 측 mutation (receiveCoupon/receiveAllCoupons)
      // in-flight 상태를 표현. owner 의 링크 복사는 이 상태와 무관하게 항상 활성.
      return (
        <Button variant="tertiaryOutlinedGray" className="h-6 px-1.5" onClick={handleCopyLink}>
          <IcCopy width={16} height={16} strokeColor="black" />
          <Typography variant="body13pxMed" className="ml-0.5">
            링크 복사
          </Typography>
        </Button>
      );
    }
    if (onReceive) {
      switch (coupon.status) {
        case 'AVAILABLE':
          return (
            <Button
              variant="tertiaryOutlinedGray"
              className="h-6 px-1.5 disabled:border-gray-300 disabled:bg-white disabled:text-black disabled:opacity-100"
              disabled={disabled}
              onClick={() => onReceive(coupon.couponNo)}
            >
              <IcDownload width={16} height={16} strokeColor="black" />
              <Typography variant="body13pxMed" className="ml-0.5">
                쿠폰 받기
              </Typography>
            </Button>
          );
        case 'RECEIVED':
          return (
            <Typography variant="body13pxReg" colorScheme="gray-500">
              발급 완료
            </Typography>
          );
        case 'EXHAUSTED':
          return (
            <Typography variant="body13pxMed" colorScheme="gray-500">
              쿠폰 소진
            </Typography>
          );
        default:
          return null;
      }
    }
    // NOTE: enableLinkCopy 도 onReceive 도 없는 경우 (방문자 view 등) — 우상단 영역 비움
    return null;
  };

  return (
    <div {...gtmDataAttrs} className={twMerge('px-[16px] py-[4px]!', gtmClassName, className)}>
      <div className="rounded border border-gray-200 bg-white">
        {/* 상단: 할인율 + (쿠폰 소진 라벨) + 액션 버튼 */}
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-1">
            <Typography variant="title18pxSemi" colorScheme={isExhausted ? 'gray-500' : 'black'}>
              {coupon.salesRate}%
            </Typography>
            {/* 판매자 (enableLinkCopy) 소진 case 에서만 좌측 Label 노출.
                방문자(onReceive) 소진 case 는 우측 액션 영역에 "쿠폰 소진" 텍스트 노출 (Figma 17220:106811). */}
            {isExhausted && enableLinkCopy && (
              <Typography variant="textEtc11pxReg" asChild>
                <Label variant="grayOpacity" className="shrink-0">
                  쿠폰 소진
                </Label>
              </Typography>
            )}
          </div>

          {renderActionArea()}
        </div>

        {/* 구분선 */}
        <div className="px-[16px]!">
          <Divider className="h-px! bg-[#8a8a8a]/10" />
        </div>

        {/* 하단: 쿠폰 정보 */}
        <div className="flex flex-col gap-0.5 px-4 py-3">
          <Typography variant="body13pxSemi" colorScheme={isExhausted ? 'gray-500' : 'black'}>
            {coupon.couponName}
          </Typography>
          <div className="flex items-end justify-between gap-3">
            <Typography variant="body13pxMed" colorScheme={isExhausted ? 'gray-500' : 'gray-600'}>
              {coupon.expirationText}
            </Typography>
            {onClickAppliedGoods && (
              <button type="button" onClick={onClickAppliedGoods} className="shrink-0">
                <Typography variant="body13pxReg" colorScheme="black" className="underline">
                  적용상품
                </Typography>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
